import React, { useState } from 'react';
import { ActivityItem, LanguageCode } from '../types';
import { translations } from '../i18n/translations';
import { getLocalized, getLocalizedCategory } from '../utils/i18n';
import { Search, Calendar, MapPin, Users, X, CheckCircle, Share2, User, Phone, CheckCircle2, Printer, ChevronLeft, ChevronRight } from 'lucide-react';

interface ActivitiesViewProps {
  currentLang: LanguageCode;
  activities: ActivityItem[];
  onNavClick: (tab: string) => void;
}

export const ActivitiesView: React.FC<ActivitiesViewProps> = ({
  currentLang,
  activities,
}) => {
  const t = translations[currentLang];
  const [selectedCategory, setSelectedCategory] = useState<string>('Tüm');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedActivity, setSelectedActivity] = useState<ActivityItem | null>(null);

  // Application Form States
  const [isApplying, setIsApplying] = useState(false);
  const [applicantName, setApplicantName] = useState('');
  const [applicantPhone, setApplicantPhone] = useState('');
  const [isAppliedSuccess, setIsAppliedSuccess] = useState(false);
  const [shareToast, setShareToast] = useState(false);

  const rawCategories = ['Tüm', 'Eğitim', 'Kültürel', 'Gençlik', 'Sosyal Yardım', 'Kadın'];

  const handleShare = (titleText?: string) => {
    if (navigator.share) {
      navigator.share({
        title: titleText || (selectedActivity ? getLocalized(selectedActivity.title, currentLang) : t.activitiesMainTitle),
        url: window.location.href,
      }).catch(() => {});
    } else {
      navigator.clipboard?.writeText(window.location.href);
      setShareToast(true);
      setTimeout(() => setShareToast(false), 3000);
    }
  };

  const handleApplySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!applicantName || !applicantPhone) return;

    if (selectedActivity) {
      selectedActivity.participantsCount += 1;
    }

    setIsAppliedSuccess(true);
    setIsApplying(false);
  };

  const handleCloseModal = () => {
    setSelectedActivity(null);
    setIsApplying(false);
    setIsAppliedSuccess(false);
    setApplicantName('');
    setApplicantPhone('');
  };

  const filtered = activities.filter((act) => {
    const matchesCategory = selectedCategory === 'Tüm' || act.category === selectedCategory;
    const titleText = getLocalized(act.title, currentLang);
    const descText = getLocalized(act.description, currentLang);
    const matchesSearch =
      titleText.toLowerCase().includes(searchQuery.toLowerCase()) ||
      descText.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // Next & Prev Activity navigation
  const currentIndex = selectedActivity ? filtered.findIndex((a) => a.id === selectedActivity.id) : -1;
  const handlePrevActivity = () => {
    if (currentIndex > 0) {
      setSelectedActivity(filtered[currentIndex - 1]);
      setIsApplying(false);
      setIsAppliedSuccess(false);
    }
  };
  const handleNextActivity = () => {
    if (currentIndex >= 0 && currentIndex < filtered.length - 1) {
      setSelectedActivity(filtered[currentIndex + 1]);
      setIsApplying(false);
      setIsAppliedSuccess(false);
    }
  };

  return (
    <div className="space-y-8 pb-12">
      {/* Page Header */}
      <div className="text-center max-w-3xl mx-auto space-y-3">
        <span className="px-3 py-1 rounded-full bg-[#132238] text-[#38BDF8] text-xs font-bold uppercase tracking-wider border border-[#38BDF8]/40 shadow-[0_0_10px_rgba(56,189,248,0.3)]">
          {t.activitiesBadge}
        </span>
        <h1 className="text-3xl sm:text-5xl font-extrabold text-white font-heading">
          {t.activitiesMainTitle}
        </h1>
        <p className="text-sm text-gray-300">
          {t.activitiesSubtitle}
        </p>
      </div>

      {/* Filter and Search Bar */}
      <div className="glass-card rounded-2xl p-4 border border-[#38BDF8]/30 flex flex-col md:flex-row items-center justify-between gap-4">
        {/* Category Pills */}
        <div className="flex items-center gap-2 overflow-x-auto w-full md:w-auto pb-2 md:pb-0 scrollbar-none">
          {rawCategories.map((cat) => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`px-3.5 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap transition-all ${
                selectedCategory === cat
                  ? 'bg-[#38BDF8] text-[#0B1426] font-bold shadow-[0_0_12px_rgba(56,189,248,0.6)]'
                  : 'bg-[#0B1426] text-gray-300 hover:bg-[#132238] border border-[#38BDF8]/20'
              }`}
            >
              {getLocalizedCategory(cat, currentLang)}
            </button>
          ))}
        </div>

        {/* Search Field */}
        <div className="relative w-full md:w-64">
          <Search className="absolute left-3.5 top-2.5 w-4 h-4 text-[#38BDF8]" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t.searchActivities}
            className="w-full pl-10 pr-4 py-2 rounded-xl bg-[#0B1426] border border-[#38BDF8]/30 text-white text-xs placeholder-gray-400 focus:outline-none focus:border-[#38BDF8] focus:shadow-[0_0_10px_rgba(56,189,248,0.4)]"
          />
        </div>
      </div>

      {/* Activity Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filtered.map((item) => (
          <div
            key={item.id}
            className="glass-card rounded-3xl overflow-hidden border border-[#38BDF8]/30 flex flex-col justify-between group hover:border-[#38BDF8] hover:shadow-[0_0_20px_rgba(56,189,248,0.4)] transition-all duration-300"
          >
            <div className="relative h-48 overflow-hidden">
              <img
                src={item.image}
                alt={getLocalized(item.title, currentLang)}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <span className="absolute top-3 left-3 px-3 py-1 rounded-full bg-[#0B1426]/90 backdrop-blur-md text-[#38BDF8] text-xs font-bold border border-[#38BDF8]/40 shadow-[0_0_8px_rgba(56,189,248,0.4)]">
                {getLocalizedCategory(item.category, currentLang)}
              </span>
            </div>

            <div className="p-5 flex-1 flex flex-col justify-between space-y-3">
              <div>
                <h3 className="text-base font-bold text-white group-hover:text-[#38BDF8] transition-colors">
                  {getLocalized(item.title, currentLang)}
                </h3>
                <p className="text-xs text-gray-300 line-clamp-2 mt-2 leading-relaxed">
                  {getLocalized(item.description, currentLang)}
                </p>
              </div>

              <div className="space-y-1.5 pt-3 border-t border-[#38BDF8]/15 text-[11px] text-gray-300">
                <div className="flex items-center gap-1.5">
                  <Calendar className="w-3.5 h-3.5 text-[#38BDF8]" />
                  <span>{item.date}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <MapPin className="w-3.5 h-3.5 text-[#38BDF8]" />
                  <span className="truncate">{item.location}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Users className="w-3.5 h-3.5 text-[#38BDF8]" />
                  <span>{item.participantsCount} {t.participantCount}</span>
                </div>
              </div>

              <button
                onClick={() => {
                  setSelectedActivity(item);
                  setIsApplying(false);
                  setIsAppliedSuccess(false);
                }}
                className="w-full py-2.5 rounded-xl bg-[#0B1426] border border-[#38BDF8] text-[#38BDF8] text-xs font-bold hover:bg-[#38BDF8] hover:text-[#0B1426] transition-all shadow-[0_0_10px_rgba(56,189,248,0.2)]"
              >
                {t.details}
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Share Toast Banner */}
      {shareToast && (
        <div className="fixed bottom-6 right-6 z-50 bg-[#38BDF8] text-[#0B1426] px-5 py-3 rounded-2xl font-extrabold text-xs shadow-[0_0_20px_rgba(56,189,248,0.8)] flex items-center gap-2 border border-white animate-bounce">
          <Share2 className="w-4 h-4 stroke-[3]" />
          <span>Bağlantı kopyalandı!</span>
        </div>
      )}

      {/* Detail & Application Overlay (Clean, Flat, Fixed Header with Geri/İleri/Kapat & Oval Glowing Image) */}
      {selectedActivity && (
        <div className="fixed inset-0 z-50 bg-[#0B1426] text-white flex flex-col overflow-hidden animate-in fade-in duration-200">
          {/* Fixed Top Header (En Üstte Sabit) */}
          <div className="sticky top-0 z-30 bg-[#0B1426]/95 backdrop-blur-md px-3 sm:px-6 py-3 border-b border-[#38BDF8]/40 shadow-[0_4px_20px_rgba(56,189,248,0.25)] flex items-center justify-between">
            {/* Left: Geri / İleri */}
            <div className="flex items-center gap-2">
              <button
                onClick={handlePrevActivity}
                disabled={currentIndex <= 0}
                className={`px-3 py-1.5 rounded-xl border border-[#38BDF8]/40 text-xs font-bold flex items-center gap-1 transition-all ${
                  currentIndex <= 0
                    ? 'opacity-40 cursor-not-allowed bg-[#132238] text-gray-400'
                    : 'bg-[#132238] text-[#38BDF8] hover:bg-[#38BDF8] hover:text-[#0B1426] hover:shadow-[0_0_12px_rgba(56,189,248,0.6)]'
                }`}
              >
                <ChevronLeft className="w-4 h-4" />
                <span className="hidden sm:inline">Geri</span>
              </button>

              <button
                onClick={handleNextActivity}
                disabled={currentIndex >= filtered.length - 1}
                className={`px-3 py-1.5 rounded-xl border border-[#38BDF8]/40 text-xs font-bold flex items-center gap-1 transition-all ${
                  currentIndex >= filtered.length - 1
                    ? 'opacity-40 cursor-not-allowed bg-[#132238] text-gray-400'
                    : 'bg-[#132238] text-[#38BDF8] hover:bg-[#38BDF8] hover:text-[#0B1426] hover:shadow-[0_0_12px_rgba(56,189,248,0.6)]'
                }`}
              >
                <span className="hidden sm:inline">İleri</span>
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>

            {/* Center: Category Badge */}
            <span className="px-3 py-1 rounded-full bg-[#132238] text-[#38BDF8] text-xs font-bold border border-[#38BDF8]/50 shadow-[0_0_10px_rgba(56,189,248,0.4)] truncate max-w-[150px] sm:max-w-xs">
              {getLocalizedCategory(selectedActivity.category, currentLang)}
            </span>

            {/* Right: Kapat (Close) */}
            <button
              onClick={handleCloseModal}
              className="px-3.5 py-1.5 rounded-full bg-[#38BDF8] text-[#0B1426] font-extrabold text-xs hover:bg-[#A5F3FC] hover:shadow-[0_0_15px_rgba(165,243,252,0.8)] transition-all flex items-center gap-1"
            >
              <span>{t.close || 'Kapat'}</span>
              <X className="w-4 h-4 stroke-[3]" />
            </button>
          </div>

          {/* Scrollable Content Body (Düz, Çerçevesiz, Sağda ve Solda 0.5 Boşluk) */}
          <div className="flex-1 overflow-y-auto px-2 sm:px-4 py-6 max-w-4xl mx-auto w-full space-y-6 scrollbar-thin">
            {/* Oval Glowing Image Frame */}
            {selectedActivity.image && (
              <div className="relative w-full max-h-[380px] rounded-[2.5rem] overflow-hidden border-2 border-[#38BDF8] shadow-[0_0_25px_rgba(56,189,248,0.5)]">
                <img
                  src={selectedActivity.image}
                  alt="Detail"
                  className="w-full h-full object-cover max-h-[380px]"
                />
              </div>
            )}

            {/* Title & Metadata */}
            <div className="space-y-2">
              <h2 className="text-2xl sm:text-4xl font-extrabold text-white font-heading leading-tight">
                {getLocalized(selectedActivity.title, currentLang)}
              </h2>
              <div className="flex items-center gap-4 text-xs text-[#38BDF8]">
                <span>📅 {selectedActivity.date}</span>
                <span>📍 {selectedActivity.location}</span>
                <span>👥 {selectedActivity.participantsCount} {t.participantCount}</span>
              </div>
            </div>

            {/* Content Text (Düz, Çerçevesiz) */}
            <div className="text-sm sm:text-base text-gray-200 leading-relaxed whitespace-pre-line py-2">
              {getLocalized(selectedActivity.content, currentLang)}
            </div>

            {/* Application Success View */}
            {isAppliedSuccess ? (
              <div className="p-6 rounded-3xl bg-[#132238] border-2 border-[#38BDF8] text-center space-y-3 shadow-[0_0_25px_rgba(56,189,248,0.5)]">
                <CheckCircle2 className="w-12 h-12 text-[#38BDF8] mx-auto animate-bounce" />
                <h3 className="text-lg font-extrabold text-white">{t.appliedSuccessMsg}</h3>
                <div className="p-3 bg-[#0B1426] rounded-xl border border-[#38BDF8]/40 text-xs font-mono text-[#38BDF8]">
                  KAYIT KODU: ACT-{selectedActivity.id.toUpperCase()}-2026
                </div>
                <button
                  onClick={handleCloseModal}
                  className="px-5 py-2.5 rounded-full bg-[#38BDF8] text-[#0B1426] font-bold text-xs hover:bg-[#A5F3FC] transition-all"
                >
                  {t.close}
                </button>
              </div>
            ) : isApplying ? (
              /* Inline Application Form */
              <form onSubmit={handleApplySubmit} className="p-5 rounded-3xl bg-[#132238] border-2 border-[#38BDF8] space-y-4 shadow-[0_0_20px_rgba(56,189,248,0.4)]">
                <h3 className="text-sm font-bold text-[#38BDF8] flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  <span>{t.applyActivity}</span>
                </h3>

                <div className="space-y-3 text-xs">
                  <div>
                    <label className="text-gray-300 font-medium block mb-1">{t.fullNameLabel} *</label>
                    <div className="relative">
                      <User className="w-4 h-4 absolute left-3.5 top-3 text-[#38BDF8]" />
                      <input
                        type="text"
                        required
                        value={applicantName}
                        onChange={(e) => setApplicantName(e.target.value)}
                        placeholder={t.fullNameLabel}
                        className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-[#0B1426] border border-[#38BDF8]/40 text-white focus:outline-none focus:border-[#38BDF8]"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-gray-300 font-medium block mb-1">{t.phoneNumber} *</label>
                    <div className="relative">
                      <Phone className="w-4 h-4 absolute left-3.5 top-3 text-[#38BDF8]" />
                      <input
                        type="tel"
                        required
                        value={applicantPhone}
                        onChange={(e) => setApplicantPhone(e.target.value)}
                        placeholder="+33 6 12 34 56 78"
                        className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-[#0B1426] border border-[#38BDF8]/40 text-white focus:outline-none focus:border-[#38BDF8]"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => setIsApplying(false)}
                    className="px-4 py-2 rounded-xl bg-[#0B1426] text-gray-300 text-xs font-bold border border-[#38BDF8]/30 hover:bg-[#132238]"
                  >
                    {t.cancel}
                  </button>
                  <button
                    type="submit"
                    className="px-5 py-2 rounded-xl bg-[#38BDF8] text-[#0B1426] text-xs font-extrabold hover:bg-[#A5F3FC] transition-all shadow-[0_0_12px_rgba(56,189,248,0.6)]"
                  >
                    {t.applyActivity}
                  </button>
                </div>
              </form>
            ) : (
              <div className="p-4 rounded-2xl bg-[#132238] border border-[#38BDF8]/40 flex items-center justify-between gap-4">
                <div className="text-xs text-gray-300">
                  <p className="font-bold text-[#38BDF8]">{selectedActivity.location}</p>
                  <p>👥 Katılımcı Sayısı: {selectedActivity.participantsCount}</p>
                </div>

                <button
                  onClick={() => setIsApplying(true)}
                  className="px-6 py-2.5 rounded-2xl bg-[#38BDF8] text-[#0B1426] font-extrabold text-xs hover:bg-[#A5F3FC] transition-all shadow-[0_0_15px_rgba(56,189,248,0.6)] flex items-center gap-2"
                >
                  <CheckCircle className="w-4 h-4" />
                  <span>{t.applyActivity}</span>
                </button>
              </div>
            )}

            {/* Bottom Actions: Paylaş & Yazdır */}
            <div className="pt-8 pb-12 border-t border-[#38BDF8]/20 flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => handleShare(getLocalized(selectedActivity.title, currentLang))}
                  className="px-5 py-2.5 rounded-2xl bg-[#132238] border border-[#38BDF8] text-[#38BDF8] font-extrabold text-xs hover:bg-[#38BDF8] hover:text-[#0B1426] transition-all shadow-[0_0_12px_rgba(56,189,248,0.4)] flex items-center gap-2"
                >
                  <Share2 className="w-4 h-4" />
                  <span>Paylaş</span>
                </button>

                <button
                  onClick={() => window.print()}
                  className="px-5 py-2.5 rounded-2xl bg-[#132238] border border-[#38BDF8]/50 text-gray-200 font-bold text-xs hover:bg-[#1C3352] hover:text-white transition-all flex items-center gap-2"
                >
                  <Printer className="w-4 h-4 text-[#38BDF8]" />
                  <span>Yazdır</span>
                </button>
              </div>

              <button
                onClick={handleCloseModal}
                className="px-6 py-2.5 rounded-full bg-[#38BDF8] text-[#0B1426] font-extrabold text-xs hover:bg-[#A5F3FC] hover:shadow-[0_0_20px_rgba(165,243,252,0.8)] transition-all"
              >
                {t.close || 'Kapat'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
